import { Grid } from '@mui/material';
import { useFormContext } from 'react-hook-form';
import LangEnum from '../../lang/LangEnum';
import { useTranslation } from '../../modules/translation/TranslationContext';
import teceoInputConfig from '../../utils/TeceoInputConfig';
import { internationalOnlyNumbersRegex } from '../../utils/regexes';
import RHFMaskField from '../ReactHookForm/RHFMaskField';
import { RHFSelect } from '../ReactHookForm/RHFSelect';
import RHFTextField from '../ReactHookForm/RHFTextField';
import { AddressProps, addressTypes } from './address.interface';
import useDefaultAddressType from './hooks/useDefaultAddressType';

const USAddress = ({
  disabled,
  complementDisabled,
  hiddenFields,
  spacing = 2,
  columnSpacing,
  rowSpacing,
  name = 'address',
}: AddressProps) => {
  const { translate, translateOptions } = useTranslation();
  const { control } = useFormContext();
  useDefaultAddressType(name);

  return (
    <Grid container spacing={spacing} columnSpacing={columnSpacing} rowSpacing={rowSpacing}>
      <Grid
        size={{
          sm: 4,
          lg: 2,
          xs: 12,
        }}
      >
        <RHFMaskField
          control={control}
          name={`${name}.zipCode`}
          mask={internationalOnlyNumbersRegex}
          textFieldProps={{
            ...teceoInputConfig,
            disabled,
            label: translate(LangEnum.ADDRESS_FORM_ZIP_CODE_LABEL),
          }}
        />
      </Grid>
      <Grid
        size={{
          sm: 8,
          lg: 5,
          xs: 12,
        }}
      >
        <RHFTextField
          control={control}
          name={`${name}.streetDescription`}
          textFieldProps={{
            ...teceoInputConfig,
            disabled,
            label: translate(LangEnum.ADDRESS_FORM_STREET_DESCRIPTION_LABEL),
            inputProps: { maxLength: 200 },
          }}
        />
      </Grid>
      <Grid
        size={{
          sm: 6,
          lg: 2,
          xs: 6,
        }}
      >
        <RHFTextField
          control={control}
          name={`${name}.streetNumber`}
          textFieldProps={{
            ...teceoInputConfig,
            disabled,
            type: 'text',
            label: translate(LangEnum.ADDRESS_FORM_NUMBER_LABEL),
          }}
        />
      </Grid>
      <Grid
        size={{
          sm: 6,
          lg: 3,
          xs: 6,
        }}
      >
        <RHFTextField
          control={control}
          name={`${name}.complement`}
          textFieldProps={{
            ...teceoInputConfig,
            disabled: complementDisabled || disabled,
            label: translate(LangEnum.ADDRESS_FORM_COMPLEMENT_LABEL),
            inputProps: { maxLength: 200 },
          }}
        />
      </Grid>
      <Grid
        size={{
          sm: 12,
          lg: 5,
          xs: 6,
        }}
      >
        <RHFTextField
          control={control}
          name={`${name}.neighborhood`}
          textFieldProps={{
            ...teceoInputConfig,
            disabled,
            label: translate(LangEnum.ADDRESS_FORM_NEIGHBORHOOD_LABEL),
            inputProps: { maxLength: 200 },
          }}
        />
      </Grid>
      <Grid
        size={{
          sm: 6,
          lg: 3,
          xs: 6,
        }}
      >
        <RHFTextField
          control={control}
          name={`${name}.city`}
          textFieldProps={{
            ...teceoInputConfig,
            disabled,
            label: translate(LangEnum.ADDRESS_FORM_CITY_LABEL),
            inputProps: { maxLength: 200 },
          }}
        />
      </Grid>
      <Grid
        size={{
          sm: 3,
          lg: 2,
          xs: 6,
        }}
      >
        <RHFTextField
          control={control}
          name={`${name}.state`}
          textFieldProps={{
            ...teceoInputConfig,
            disabled,
            label: translate(LangEnum.ADDRESS_FORM_STATE_LABEL),
            inputProps: { maxLength: 200 },
          }}
        />
      </Grid>
      <Grid
        size={{
          sm: 3,
          lg: 2,
          xs: 6,
        }}
      >
        <RHFTextField
          control={control}
          name={`${name}.country`}
          textFieldProps={{
            ...teceoInputConfig,
            disabled,
            label: translate(LangEnum.ADDRESS_FORM_COUNTRY_LABEL),
            inputProps: { maxLength: 200 },
          }}
        />
      </Grid>
      <Grid
        sx={{ display: hiddenFields?.includes('code') ? 'none' : undefined }}
        size={{
          sm: 4,
          lg: 2,
          xs: 6,
        }}
      >
        <RHFTextField
          control={control}
          name={`${name}.code`}
          testId="text_field_address_code"
          textFieldProps={{
            ...teceoInputConfig,
            disabled: complementDisabled || disabled,
            label: translate(LangEnum.ADDRESS_FORM_CODE_LABEL),
            inputProps: { maxLength: 200 },
          }}
        />
      </Grid>
      <Grid
        size={{
          sm: 4,
          lg: 2,
          xs: 6,
        }}
      >
        <RHFSelect
          control={control}
          name={`${name}.type`}
          disabled={disabled}
          items={translateOptions(addressTypes)}
          label={translate(LangEnum.ADDRESS_FORM_TYPE_LABEL)}
        />
      </Grid>
    </Grid>
  );
};

export default USAddress;
